import type {
  GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdStatistics200,
  GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsers200,
  GetApiV1ThreeLetterCodeClientAdminsClientNewslettersId200,
} from '@fukuri-admins/apis/client/gen/model';
import { formatDate } from '@fukuri-admins/utils/common/date';
import { InternalNewsDetailPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof InternalNewsDetailPresentation>;

type Args = {
  newsletterData: GetApiV1ThreeLetterCodeClientAdminsClientNewslettersId200;
  isPublished: boolean;
  onEdit: PresentationProps['article']['onEdit'];
  users: GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdUsers200;
  statistics: GetApiV1ThreeLetterCodeClientAdminsClientNewslettersClientNewsletterIdStatistics200;
};

type IConvertToInternalNewsDetailProps = (args: Args) => {
  article: PresentationProps['article'];
  userUsages: PresentationProps['userUsages'];
  viewRateCard: PresentationProps['viewRateCard'];
  unreadCount: number;
  readCount: number;
};

/**
 * 社内報 API レスポンスを社内報詳細 UI 型に変換する。
 * article は newsletterData.client_newsletter から分割代入して生成する。
 * userUsages は users.client_newsletter_users から UsageTable の行データに変換する。
 * viewRateCard は statistics.client_newsletter_statistics から ViewRateCard の props に変換する。
 */
export const convertToInternalNewsDetailProps: IConvertToInternalNewsDetailProps = ({
  newsletterData,
  isPublished,
  onEdit,
  users,
  statistics,
}) => {
  const { title, body, main_image, files, publish_at } = newsletterData.client_newsletter;

  const article = {
    title,
    body: body ?? '',
    mainImageUrl: main_image?.url ?? null,
    files:
      files?.map(file => ({
        name: file.filename ?? 'ファイル',
        url: file.url ?? '',
      })) ?? [],
    publishAt: formatDate({
      date: new Date(publish_at * 1000),
      formatType: 'yyyy/M/d H:mm',
    }),
    isPublished,
    onEdit,
  };

  const userUsages = users.client_newsletter_users.map(user => ({
    name: user.full_name,
    readStatus: user.read_at !== 0 ? 'read' : 'unread',
    readAt:
      user.read_at !== 0
        ? formatDate({
            date: new Date(user.read_at * 1000),
            formatType: 'yyyy/M/d H:mm',
          })
        : '-',
  }));

  const { read_count, user_count, read_rate } = statistics.client_newsletter_statistics;

  const viewRateCard = {
    readCount: read_count,
    totalCount: user_count,
    ratePercent: read_rate,
  };

  return {
    article,
    userUsages,
    viewRateCard,
    unreadCount: user_count - read_count,
    readCount: read_count,
  };
};
